import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import PersonIcon from "@mui/icons-material/Person"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import EventBusyIcon from "@mui/icons-material/EventBusy"

import MainLayout from "../../layouts/MainLayout"

import {
  createDriver,
  deactivateDriver,
  getDriverFormData,
  getDrivers,
  updateDriver,
} from "../../services/driverService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultForm = {
  full_name: "",
  phone: "",
  license_number: "",
  license_category: "",
  status: "available",
  is_active: true,
  notes: "",
}


const statusLabels = {
  available: "Disponible",
  on_trip: "En mission",
  absent: "Absent",
  inactive: "Inactif",
}


const statusColors = {
  available: "success",
  on_trip: "info",
  absent: "warning",
  inactive: "default",
}


// =========================================================
// HELPERS
// =========================================================

function getApiErrorMessage(
  error,
  fallback = "Action impossible."
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


function getLicenseCategoryLabel(
  value,
  categories = []
) {
  if (!value) {
    return "Non définie"
  }

  return (
    categories.find(
      (item) =>
        item.value === value
    )?.label ||
    value
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.4,
          sm: 1.6,
        },

        py: {
          xs: 1.2,
          sm: 1.3,
        },

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",

        transition:
          "transform 0.18s ease, box-shadow 0.18s ease",

        "&:hover": {
          transform:
            "translateY(-1px)",

          boxShadow:
            "0 9px 24px rgba(15, 23, 42, 0.065)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.2,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,

            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.75,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",

              textOverflow: "ellipsis",

              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function DriversPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    drivers,
    setDrivers,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({})

  const [
    formData,
    setFormData,
  ] = useState({
    statuses: [],
    license_categories: [],
    permissions: {},
  })


  // =======================================================
  // UI STATE
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedDriver,
    setSelectedDriver,
  ] = useState(null)

  const [
    form,
    setForm,
  ] = useState({
    ...defaultForm,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    filters,
    setFilters,
  ] = useState({
    search: "",
    status: "",
    is_active: "",
  })


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // PERMISSIONS
  // =======================================================

  const permissions =
    formData.permissions || {}


  // =======================================================
  // SNACKBAR HELPERS
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const closeSnackbar = () => {
    setSnackbar(
      (current) => ({
        ...current,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getDriverFormData()

        setFormData({
          statuses:
            data.statuses || [],

          license_categories:
            data.license_categories ||
            [],

          permissions:
            data.permissions || {},
        })
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // LOAD DRIVERS
  // =======================================================

  const loadDrivers =
    async (
      customFilters = null
    ) => {
      try {
        setLoading(true)

        const params =
          customFilters ||
          filters

        const data =
          await getDrivers(
            params
          )

        setDrivers(
          Array.isArray(
            data?.items
          )
            ? data.items
            : []
        )

        setSummary(
          data?.summary || {}
        )

        setPaginationModel(
          (previous) => ({
            ...previous,

            page: 0,

            pageSize:
              ROWS_PER_PAGE,
          })
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les chauffeurs."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadFormData()

    loadDrivers({
      search: "",
      status: "",
      is_active: "",
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setForm({
      ...defaultForm,
    })

    setSelectedDriver(
      null
    )
  }


  // =======================================================
  // CREATE
  // =======================================================

  const openCreateDialog =
    () => {
      resetForm()

      setOpen(true)
    }


  // =======================================================
  // EDIT
  // =======================================================

  const openEditDialog =
    (driver) => {
      setSelectedDriver(
        driver
      )

      setForm({
        full_name:
          driver.full_name || "",

        phone:
          driver.phone || "",

        license_number:
          driver.license_number ||
          "",

        license_category:
          driver.license_category ||
          "",

        status:
          driver.status ||
          "available",

        is_active:
          typeof driver.is_active ===
          "boolean"
            ? driver.is_active
            : true,

        notes:
          driver.notes ||
          "",
      })

      setOpen(true)
    }


  // =======================================================
  // CLOSE DIALOG
  // =======================================================

  const closeDialog = () => {
    if (saving) {
      return
    }

    setOpen(false)

    resetForm()
  }


  // =======================================================
  // UPDATE FORM
  // =======================================================

  const updateForm = (
    field,
    value
  ) => {
    setForm(
      (current) => ({
        ...current,

        [field]: value,
      })
    )
  }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !form.full_name?.trim()
    ) {
      showSnackbar(
        "Le nom du chauffeur est obligatoire.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // BUILD PAYLOAD
  // =======================================================

  const buildPayload = () => {
    return {
      full_name:
        form.full_name.trim(),

      phone:
        form.phone?.trim() ||
        null,

      license_number:
        form.license_number
          ?.trim()
          .toUpperCase() ||
        null,

      license_category:
        form.license_category
          ?.trim() ||
        null,

      status:
        form.status ||
        "available",

      is_active:
        Boolean(
          form.is_active
        ),

      notes:
        form.notes?.trim() ||
        null,
    }
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      try {
        setSaving(true)

        const payload =
          buildPayload()

        if (
          selectedDriver?.id
        ) {
          await updateDriver(
            selectedDriver.id,
            payload
          )

          showSnackbar(
            "Chauffeur modifié avec succès."
          )
        } else {
          await createDriver(
            payload
          )

          showSnackbar(
            "Chauffeur créé avec succès."
          )
        }

        /*
         * Fermeture directe.
         * closeDialog() ne peut pas fermer
         * pendant que saving === true.
         */
        setOpen(false)

        resetForm()

        await loadDrivers()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible d'enregistrer le chauffeur."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DEACTIVATE
  // =======================================================

  const handleDeactivate =
    async (driver) => {
      const ok =
        window.confirm(
          `Désactiver le chauffeur ${driver.full_name} ?`
        )

      if (!ok) {
        return
      }

      try {
        await deactivateDriver(
          driver.id
        )

        showSnackbar(
          "Chauffeur désactivé avec succès."
        )

        await loadDrivers()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de désactiver le chauffeur."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters =
    async () => {
      const emptyFilters = {
        search: "",
        status: "",
        is_active: "",
      }

      setFilters(
        emptyFilters
      )

      setPaginationModel({
        page: 0,

        pageSize:
          ROWS_PER_PAGE,
      })

      await loadDrivers(
        emptyFilters
      )
    }


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedDrivers =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return drivers.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      drivers,
      paginationModel.page,
    ])


  // =======================================================
  // KEEP VALID PAGE
  // =======================================================

  useEffect(() => {
    const totalPages =
      Math.max(
        1,

        Math.ceil(
          drivers.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    drivers.length,
    paginationModel.page,
  ])


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns = [
    {
      field:
        "full_name",

      headerName:
        "Chauffeur",

      minWidth: 190,

      flex: 1.2,

      renderCell:
        (params) => (
          <Stack
            spacing={0.05}

            sx={{
              minWidth: 0,

              width: "100%",
            }}
          >
            <Typography
              variant="body2"

              fontWeight={900}

              color="#0f172a"

              noWrap

              title={
                params.value ||
                ""
              }
            >
              {params.value ||
                "-"}
            </Typography>

            <Typography
              variant="caption"

              color="text.secondary"

              noWrap
            >
              Chauffeur #{params.row.id}
            </Typography>
          </Stack>
        ),
    },


    {
      field:
        "phone",

      headerName:
        "Téléphone",

      minWidth: 140,

      flex: 0.8,

      renderCell:
        (params) => (
          <Typography
            variant="body2"
            noWrap
          >
            {params.value ||
              "-"}
          </Typography>
        ),
    },


    {
      field:
        "license",

      headerName:
        "Permis",

      minWidth: 170,

      flex: 1,

      sortable: false,

      renderCell:
        (params) => (
          <Stack
            spacing={0.05}

            sx={{
              minWidth: 0,

              width: "100%",
            }}
          >
            <Typography
              variant="body2"

              fontWeight={800}

              noWrap
            >
              {params.row
                .license_number ||
                "Non renseigné"}
            </Typography>

            <Typography
              variant="caption"

              color="text.secondary"

              noWrap
            >
              Catégorie{" "}
              {getLicenseCategoryLabel(
                params.row
                  .license_category,
                formData.license_categories
              )}
            </Typography>
          </Stack>
        ),
    },


    {
      field:
        "status",

      headerName:
        "Statut",

      width: 130,

      renderCell:
        (params) => (
          <Chip
            size="small"

            label={
              statusLabels[
                params.value
              ] ||
              params.value ||
              "-"
            }

            color={
              statusColors[
                params.value
              ] ||
              "default"
            }

            sx={{
              fontWeight: 800,
            }}
          />
        ),
    },


    {
      field:
        "is_active",

      headerName:
        "Actif",

      width: 85,

      renderCell:
        (params) => (
          <Chip
            size="small"

            label={
              params.value
                ? "Oui"
                : "Non"
            }

            color={
              params.value
                ? "success"
                : "default"
            }

            variant={
              params.value
                ? "filled"
                : "outlined"
            }

            sx={{
              fontWeight: 800,
            }}
          />
        ),
    },


    {
      field:
        "actions",

      headerName:
        "Actions",

      width: 185,

      sortable: false,

      filterable: false,

      disableColumnMenu:
        true,

      renderCell:
        (params) => (
          <Stack
            direction="row"

            spacing={0.65}
          >
            <Button
              size="small"

              variant="contained"

              disableElevation

              onClick={() =>
                openEditDialog(
                  params.row
                )
              }

              disabled={
                !permissions.can_update
              }

              sx={{
                minWidth: 78,
              }}
            >
              Modifier
            </Button>


            <Button
              size="small"

              color="error"

              variant="outlined"

              onClick={() =>
                handleDeactivate(
                  params.row
                )
              }

              disabled={
                !permissions.can_delete ||
                !params.row.is_active
              }

              sx={{
                minWidth: 90,
              }}
            >
              Désactiver
            </Button>
          </Stack>
        ),
    },
  ]


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight: 800,

              borderRadius: 2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap: 2,

            rowGap: 1.25,

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Chauffeurs
            </Typography>


            <Typography
              variant="body2"

              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 820,
              }}
            >
              Gérez les chauffeurs, leurs permis, leur
              disponibilité et leur affectation aux opérations
              de livraison.
            </Typography>
          </Box>


          <Button
            variant="contained"

            disableElevation

            onClick={
              openCreateDialog
            }

            disabled={
              !permissions.can_create
            }

            sx={{
              minHeight: 40,

              px: 2.2,

              minWidth: {
                xs: "100%",
                sm: 190,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouveau chauffeur
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"

          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,

            alignItems: "center",

            bgcolor:
              "rgba(14, 165, 233, 0.025)",
          }}
        >
          Seuls les chauffeurs actifs et disponibles peuvent
          être affectés normalement à une nouvelle livraison.
          Le statut permet de distinguer les chauffeurs
          disponibles, en mission ou absents.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                md:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap: 1.15,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Chauffeurs"

            value={
              summary.total ||
              0
            }

            caption="Effectif enregistré"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <PersonIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Disponibles"

            value={
              summary.available ||
              0
            }

            caption="Prêts à sortir"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleIcon fontSize="small" />
            }
          />


          <MetricCard
            label="En mission"

            value={
              summary.on_trip ||
              0
            }

            caption="Actuellement affectés"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <LocalShippingIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Absents"

            value={
              summary.absent ||
              0
            }

            caption="Non disponibles"

            accent="#ea580c"

            background="rgba(234, 88, 12, 0.09)"

            icon={
              <EventBusyIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 6px 18px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                {
                  xs: "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",

                  lg:
                    "minmax(240px, 1.5fr) minmax(170px, 0.8fr) minmax(140px, 0.65fr) auto auto",
                },

              gap: 1,

              alignItems:
                "center",
            }}
          >
            <TextField
              size="small"

              label="Recherche"

              placeholder="Nom, téléphone, permis..."

              value={
                filters.search
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    search:
                      event.target.value,
                  })
                )
              }

              fullWidth
            />


            <TextField
              select

              size="small"

              label="Statut"

              value={
                filters.status
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    status:
                      event.target.value,
                  })
                )
              }

              fullWidth
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {(formData.statuses || []).map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }

                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select

              size="small"

              label="Actif"

              value={
                filters.is_active
              }

              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    is_active:
                      event.target.value,
                  })
                )
              }

              fullWidth
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="true">
                Oui
              </MenuItem>

              <MenuItem value="false">
                Non
              </MenuItem>
            </TextField>


            <Button
              variant="contained"

              disableElevation

              onClick={() =>
                loadDrivers()
              }

              disabled={
                loading
              }

              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 95,
                },
              }}
            >
              Appliquer
            </Button>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              disabled={
                loading
              }

              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 115,
                },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            DRIVERS REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            minWidth: 0,

            overflow: "hidden",

            borderRadius: 3.5,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >

          {/* REGISTER HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des chauffeurs
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Chauffeurs, permis et disponibilité logistique
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${drivers.length} chauffeur${
                drivers.length > 1
                  ? "s"
                  : ""
              }`}

              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflow: "hidden",

                bgcolor: "#ffffff",
              }}
            >
              <DataGrid
                autoHeight

                rows={
                  drivers
                }

                columns={
                  columns
                }

                loading={
                  loading
                }

                disableRowSelectionOnClick

                disableColumnSelector

                pagination

                paginationModel={
                  paginationModel
                }

                onPaginationModelChange={
                  setPaginationModel
                }

                pageSizeOptions={[
                  ROWS_PER_PAGE,
                ]}

                rowHeight={62}

                columnHeaderHeight={48}

                localeText={{
                  noRowsLabel:
                    "Aucun chauffeur enregistré",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination:
                    {
                      labelRowsPerPage:
                        "",

                      labelDisplayedRows:
                        ({
                          from,
                          to,
                          count,
                        }) =>
                          `${from}–${to} sur ${count}`,
                    },
                }}

                sx={{
                  border: 0,

                  width: "100%",

                  minWidth: 0,

                  color: "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor:
                        "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor:
                        "#f8fafc",

                      px: 1.05,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight: 900,

                      color:
                        "#334155",

                      fontSize:
                        "0.78rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      px: 1.05,

                      minWidth: 0,

                      display: "flex",

                      alignItems:
                        "center",

                      borderColor:
                        "rgba(148, 163, 184, 0.14)",
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight: 50,

                      bgcolor:
                        "#ffffff",

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.20)",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display: "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               MOBILE / TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des chauffeurs...
                  </Typography>
                </Box>
              ) : paginatedDrivers.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun chauffeur trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.1}
                >
                  {paginatedDrivers.map(
                    (driver) => (
                      <Paper
                        key={
                          driver.id
                        }

                        variant="outlined"

                        sx={{
                          p: {
                            xs: 1.4,
                            sm: 1.6,
                          },

                          borderRadius: 3,

                          bgcolor:
                            "#ffffff",

                          borderColor:
                            "rgba(148, 163, 184, 0.22)",
                        }}
                      >
                        <Stack
                          spacing={1.35}
                        >

                          {/* TOP */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                fontWeight={900}

                                color="#0f172a"

                                sx={{
                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {driver.full_name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"

                                color="text.secondary"
                              >
                                {driver.phone ||
                                  "Téléphone non renseigné"}
                              </Typography>
                            </Box>


                            <Chip
                              size="small"

                              label={
                                statusLabels[
                                  driver.status
                                ] ||
                                driver.status ||
                                "-"
                              }

                              color={
                                statusColors[
                                  driver.status
                                ] ||
                                "default"
                              }

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* PERMIT */}

                          <Paper
                            variant="outlined"

                            sx={{
                              p: 1,

                              borderRadius:
                                2.5,

                              bgcolor:
                                "#f8fafc",
                            }}
                          >
                            <Typography
                              variant="caption"

                              color="text.secondary"

                              fontWeight={700}
                            >
                              Permis de conduire
                            </Typography>

                            <Typography
                              variant="body2"

                              fontWeight={900}

                              color="#0f172a"
                            >
                              {driver.license_number ||
                                "Non renseigné"}
                            </Typography>

                            <Typography
                              variant="caption"

                              color="text.secondary"
                            >
                              Catégorie :{" "}
                              {getLicenseCategoryLabel(
                                driver.license_category,
                                formData.license_categories
                              )}
                            </Typography>
                          </Paper>


                          {/* ACTIVE */}

                          <Box
                            sx={{
                              display: "flex",

                              alignItems: "center",

                              gap: 0.8,
                            }}
                          >
                            <Typography
                              variant="caption"

                              color="text.secondary"

                              fontWeight={700}
                            >
                              Enregistrement actif
                            </Typography>

                            <Chip
                              size="small"

                              label={
                                driver.is_active
                                  ? "Oui"
                                  : "Non"
                              }

                              color={
                                driver.is_active
                                  ? "success"
                                  : "default"
                              }

                              variant={
                                driver.is_active
                                  ? "filled"
                                  : "outlined"
                              }

                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* NOTES */}

                          {driver.notes ? (
                            <Box>
                              <Typography
                                variant="caption"

                                color="text.secondary"

                                fontWeight={700}
                              >
                                Notes
                              </Typography>

                              <Typography
                                variant="body2"

                                sx={{
                                  mt: 0.2,

                                  lineHeight: 1.5,

                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {driver.notes}
                              </Typography>
                            </Box>
                          ) : null}


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display: "grid",

                              gridTemplateColumns:
                                {
                                  xs: "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap: 0.8,
                            }}
                          >
                            <Button
                              size="small"

                              variant="contained"

                              disableElevation

                              onClick={() =>
                                openEditDialog(
                                  driver
                                )
                              }

                              disabled={
                                !permissions.can_update
                              }
                            >
                              Modifier
                            </Button>


                            <Button
                              size="small"

                              variant="outlined"

                              color="error"

                              onClick={() =>
                                handleDeactivate(
                                  driver
                                )
                              }

                              disabled={
                                !permissions.can_delete ||
                                !driver.is_active
                              }
                            >
                              Désactiver
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              {/* MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  drivers.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,

                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          DRIVER DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          closeDialog
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "90dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.4,
          }}
        >
          <Typography
            fontWeight={900}

            fontSize={20}

            color="#0f172a"
          >
            {selectedDriver
              ? "Modifier le chauffeur"
              : "Nouveau chauffeur"}
          </Typography>


          <Typography
            variant="body2"

            color="text.secondary"

            sx={{
              mt: 0.3,
            }}
          >
            {selectedDriver
              ? "Modifiez les coordonnées, le permis et la disponibilité de ce chauffeur."
              : "Ajoutez un chauffeur et renseignez les informations utiles pour les opérations logistiques."}
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >

            {/* =================================================
                IDENTIFICATION
            ================================================= */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.6,
                },

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.24)",
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.3,
                }}
              >
                Identification
              </Typography>


              <TextField
                size="small"

                label="Nom complet"

                value={
                  form.full_name
                }

                onChange={(
                  event
                ) =>
                  updateForm(
                    "full_name",
                    event.target.value
                  )
                }

                fullWidth

                required

                disabled={
                  saving
                }

                sx={{
                  mb: 1.5,
                }}
              />


              <TextField
                size="small"

                label="Téléphone"

                value={
                  form.phone
                }

                onChange={(
                  event
                ) =>
                  updateForm(
                    "phone",
                    event.target.value
                  )
                }

                fullWidth

                disabled={
                  saving
                }

                placeholder="Ex. 0990000000"
              />
            </Paper>


            {/* =================================================
                LICENSE
            ================================================= */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.6,
                },

                borderRadius: 3,

                bgcolor:
                  "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.24)",
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.3,
                }}
              >
                Permis de conduire
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Numéro du permis"

                  value={
                    form.license_number
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "license_number",
                      event.target.value.toUpperCase()
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  placeholder="Numéro du permis"
                />


                <TextField
                  select

                  size="small"

                  label="Catégorie du permis"

                  value={
                    form.license_category
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "license_category",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Non définie
                  </MenuItem>

                  {(formData.license_categories || []).map(
                    (category) => (
                      <MenuItem
                        key={
                          category.value
                        }

                        value={
                          category.value
                        }
                      >
                        {category.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>
            </Paper>


            {/* =================================================
                AVAILABILITY
            ================================================= */}

            <Paper
              variant="outlined"

              sx={{
                p: {
                  xs: 1.4,
                  sm: 1.6,
                },

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.24)",
              }}
            >
              <Typography
                fontWeight={900}

                color="#0f172a"

                sx={{
                  mb: 1.3,
                }}
              >
                Disponibilité
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns:
                    {
                      xs: "1fr",

                      md:
                        "repeat(2, minmax(0, 1fr))",
                    },

                  gap: 1.5,
                }}
              >
                <TextField
                  select

                  size="small"

                  label="Statut opérationnel"

                  value={
                    form.status
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "status",
                      event.target.value
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {(formData.statuses || []).map(
                    (status) => (
                      <MenuItem
                        key={
                          status.value
                        }

                        value={
                          status.value
                        }
                      >
                        {status.label}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select

                  size="small"

                  label="Enregistrement actif"

                  value={
                    String(
                      form.is_active
                    )
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "is_active",
                      event.target.value ===
                        "true"
                    )
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="true">
                    Oui
                  </MenuItem>

                  <MenuItem value="false">
                    Non
                  </MenuItem>
                </TextField>
              </Box>
            </Paper>


            {/* =================================================
                NOTES
            ================================================= */}

            <TextField
              label="Notes"

              value={
                form.notes
              }

              onChange={(
                event
              ) =>
                updateForm(
                  "notes",
                  event.target.value
                )
              }

              fullWidth

              multiline

              minRows={3}

              disabled={
                saving
              }

              placeholder="Informations complémentaires sur le chauffeur..."
            />
          </Stack>
        </DialogContent>


        {/* =================================================
            DIALOG ACTIONS
        ================================================= */}

        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              closeDialog
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"

            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedDriver
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          closeSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }

          onClose={
            closeSnackbar
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default DriversPage